import React, { useState, useEffect } from 'react';
import { Building2, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CompanySelector - Reusable component for selecting a company
 * Used in Purchase Orders, Invoices, etc.
 */
const CompanySelector = ({ value, onChange, required = false, disabled = false }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (value && companies.length > 0) {
      const company = companies.find(c => c.id === value);
      setSelectedCompany(company);
    }
  }, [value, companies]);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch companies');
      const data = await response.json();
      setCompanies(data);

      // Auto-select default company if no value
      if (!value) {
        const defaultCompany = data.find(c => c.is_default);
        if (defaultCompany && onChange) {
          onChange(defaultCompany.id, defaultCompany);
        }
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const companyId = e.target.value;
    const company = companies.find(c => c.id === companyId);
    setSelectedCompany(company);
    if (onChange) {
      onChange(companyId, company);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-[#2B2D42] mb-2">
        Issuing Company {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <Building2 className="h-5 w-5 text-[#6C757D]" />
        </div>
        
        <select
          value={value || ''}
          onChange={handleChange}
          required={required}
          disabled={disabled || loading}
          className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent appearance-none bg-white"
        >
          {loading ? (
            <option value="">Loading companies...</option>
          ) : (
            <>
              <option value="">Select a company</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.legal_name}
                  {company.trading_name && ` (${company.trading_name})`}
                  {company.is_default && ' ★ Default'}
                </option>
              ))}
            </>
          )}
        </select>
        
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <ChevronDown className="h-5 w-5 text-[#6C757D]" />
        </div>
      </div>

      {/* Selected Company Preview */}
      {selectedCompany && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-start space-x-3">
            {selectedCompany.logo_base64 && (
              <img
                src={selectedCompany.logo_base64}
                alt={selectedCompany.legal_name}
                className="h-10 w-10 object-contain rounded border border-gray-200 p-1 bg-white"
              />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-[#2B2D42]">
                {selectedCompany.legal_name}
              </p>
              <p className="text-xs text-[#6C757D] mt-1">
                {selectedCompany.address}, {selectedCompany.city}, {selectedCompany.country}
              </p>
              <p className="text-xs text-[#6C757D]">
                {selectedCompany.email} • {selectedCompany.phone}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySelector;